import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Eye, EyeOff, Loader2, Mail, Lock, User, Phone } from "lucide-react";
import { validatePassword, getPasswordErrorMessage } from '@/lib/passwordValidation';

// EU Countries with calling codes
const countries = [
  { code: '+43', country: 'Österreich', flag: '🇦🇹', search: 'osterreich austria' },
  { code: '+32', country: 'Belgien', flag: '🇧🇪', search: 'belgien belgium' },
  { code: '+359', country: 'Bulgarien', flag: '🇧🇬', search: 'bulgarien bulgaria' },
  { code: '+385', country: 'Kroatien', flag: '🇭🇷', search: 'kroatien croatia' },
  { code: '+357', country: 'Zypern', flag: '🇨🇾', search: 'zypern cyprus' },
  { code: '+420', country: 'Tschechien', flag: '🇨🇿', search: 'tschechien czech' },
  { code: '+45', country: 'Dänemark', flag: '🇩🇰', search: 'danemark denmark' },
  { code: '+372', country: 'Estland', flag: '🇪🇪', search: 'estland estonia' },
  { code: '+358', country: 'Finnland', flag: '🇫🇮', search: 'finnland finland' },
  { code: '+33', country: 'Frankreich', flag: '🇫🇷', search: 'frankreich france' },
  { code: '+49', country: 'Deutschland', flag: '🇩🇪', search: 'deutschland germany' },
  { code: '+30', country: 'Griechenland', flag: '🇬🇷', search: 'griechenland greece' },
  { code: '+36', country: 'Ungarn', flag: '🇭🇺', search: 'ungarn hungary' },
  { code: '+353', country: 'Irland', flag: '🇮🇪', search: 'irland ireland' },
  { code: '+39', country: 'Italien', flag: '🇮🇹', search: 'italien italy' },
  { code: '+371', country: 'Lettland', flag: '🇱🇻', search: 'lettland latvia' },
  { code: '+370', country: 'Litauen', flag: '🇱🇹', search: 'litauen lithuania' },
  { code: '+352', country: 'Luxemburg', flag: '🇱🇺', search: 'luxemburg luxembourg' },
  { code: '+356', country: 'Malta', flag: '🇲🇹', search: 'malta' },
  { code: '+31', country: 'Niederlande', flag: '🇳🇱', search: 'niederlande netherlands holland' },
  { code: '+48', country: 'Polen', flag: '🇵🇱', search: 'polen poland' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', search: 'portugal' },
  { code: '+40', country: 'Rumänien', flag: '🇷🇴', search: 'rumanien romania' },
  { code: '+421', country: 'Slowakei', flag: '🇸🇰', search: 'slowakei slovakia' },
  { code: '+386', country: 'Slowenien', flag: '🇸🇮', search: 'slowenien slovenia' },
  { code: '+34', country: 'Spanien', flag: '🇪🇸', search: 'spanien spain' },
  { code: '+46', country: 'Schweden', flag: '🇸🇪', search: 'schweden sweden' },
  { code: '+41', country: 'Schweiz', flag: '🇨🇭', search: 'schweiz switzerland' },
];

interface CheckoutAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export default function CheckoutAuthModal({ open, onOpenChange, onSuccess }: CheckoutAuthModalProps) {
  const { signUp, signIn } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+49');
  const [openCountrySelect, setOpenCountrySelect] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateRegisterForm = () => {
    const newErrors: Record<string, string> = {};

    if (!registerData.firstName.trim()) newErrors.firstName = "Vorname ist erforderlich";
    if (!registerData.lastName.trim()) newErrors.lastName = "Nachname ist erforderlich";
    if (!registerData.email.trim()) newErrors.email = "E-Mail ist erforderlich";
    else if (!validateEmail(registerData.email)) newErrors.email = "Ungültige E-Mail-Adresse";
    if (!registerData.phone.trim()) newErrors.phone = "Telefonnummer ist erforderlich";
    
    // Stärkere Passwort-Validierung mit passwordValidation.ts
    const passwordValidation = validatePassword(registerData.password);
    if (!passwordValidation.isValid) {
      const errorMsg = getPasswordErrorMessage(passwordValidation);
      newErrors.password = errorMsg || 'Das Passwort erfüllt nicht die Anforderungen.';
    }
    
    if (registerData.password !== registerData.confirmPassword) 
      newErrors.confirmPassword = "Passwörter stimmen nicht überein";
    if (!registerData.acceptTerms) newErrors.acceptTerms = "AGB müssen akzeptiert werden";
    if (!registerData.acceptPrivacy) newErrors.acceptPrivacy = "Datenschutz muss akzeptiert werden";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateLoginForm = () => {
    const newErrors: Record<string, string> = {};

    if (!loginData.email.trim()) newErrors.email = "E-Mail ist erforderlich";
    else if (!validateEmail(loginData.email)) newErrors.email = "Ungültige E-Mail-Adresse";
    if (!loginData.password) newErrors.password = "Passwort ist erforderlich";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setLoading(true);
    try {
      const { error } = await signIn(loginData.email, loginData.password);
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Anmeldung fehlgeschlagen",
            description: "E-Mail oder Passwort ist falsch.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Anmeldung fehlgeschlagen",
            description: error.message || "Ein Fehler ist aufgetreten.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Erfolgreich angemeldet",
          description: "Sie werden nun zum Checkout weitergeleitet.",
        });
        onOpenChange(false);
        onSuccess();
      }
    } catch (err) {
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRegisterForm()) return;

    setLoading(true);
    try {
      const { error } = await signUp(registerData.email, registerData.password, {
        first_name: registerData.firstName,
        last_name: registerData.lastName,
        phone: `${countryCode}${registerData.phone}`,
      });

      if (error) {
        if (error.message.includes("User already registered")) {
          toast({
            title: "Registrierung fehlgeschlagen",
            description: "Ein Konto mit dieser E-Mail-Adresse existiert bereits.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Registrierung fehlgeschlagen",
            description: error.message || "Ein Fehler ist aufgetreten.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Konto erstellt!",
          description: "Schließen Sie jetzt Ihren Kauf ab.",
        });
        onOpenChange(false);
        onSuccess();
      }
    } catch (err) {
      toast({
        title: "Registrierung fehlgeschlagen",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="p-6">
      <Tabs defaultValue="login" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">Anmelden</TabsTrigger>
          <TabsTrigger value="register">Registrieren</TabsTrigger>
        </TabsList>

        <TabsContent value="login" className="space-y-4 mt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className={errors.email ? "border-destructive pl-10" : "pl-10"}
                />
              </div>
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="login-password">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className={errors.password ? "border-destructive pl-10 pr-10" : "pl-10 pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird angemeldet...
                </>
              ) : (
                "Anmelden und fortfahren"
              )}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="register" className="space-y-4 mt-6">
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Vorname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firstName"
                    value={registerData.firstName}
                    onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                    className={errors.firstName ? "border-destructive pl-10" : "pl-10"}
                  />
                </div>
                {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="lastName"
                    value={registerData.lastName}
                    onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                    className={errors.lastName ? "border-destructive pl-10" : "pl-10"}
                  />
                </div>
                {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="register-email">E-Mail *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="register-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className={errors.email ? "border-destructive pl-10" : "pl-10"}
                />
              </div>
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer *</Label>
              <div className="flex gap-2">
                <Popover open={openCountrySelect} onOpenChange={setOpenCountrySelect}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCountrySelect}
                      className="w-[120px] justify-between"
                    >
                      {countries.find((c) => c.code === countryCode)?.flag}{' '}
                      {countryCode}
                      <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-[250px] p-0 z-[100] bg-popover border shadow-md" 
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    avoidCollisions={true}
                    collisionPadding={16}
                  >
                    <Command>
                      <CommandInput placeholder="Land suchen..." className="border-b" />
                      <CommandList className="max-h-[200px] overflow-auto">
                        <CommandEmpty>Kein Land gefunden.</CommandEmpty>
                        <CommandGroup>
                          {countries.map((country) => (
                            <CommandItem
                              key={country.code}
                              value={`${country.search} ${country.code}`}
                              onSelect={() => {
                                setCountryCode(country.code);
                                setOpenCountrySelect(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  countryCode === country.code ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {country.flag} {country.country} ({country.code})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="1234567890"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    className={errors.phone ? "border-destructive pl-10" : "pl-10"}
                  />
                </div>
              </div>
              {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="register-password">Passwort *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="register-password"
                  type={showPassword ? "text" : "password"}
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className={errors.password ? "border-destructive pl-10 pr-10" : "pl-10 pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {registerData.password && (
                <div className="text-xs space-y-1 mt-2">
                  <p className={validatePassword(registerData.password).minLength ? 'text-green-600' : 'text-muted-foreground'}>
                    ✓ Mindestens 8 Zeichen
                  </p>
                  <p className={validatePassword(registerData.password).hasUpper ? 'text-green-600' : 'text-muted-foreground'}>
                    ✓ Mindestens ein Großbuchstabe
                  </p>
                  <p className={validatePassword(registerData.password).hasNumber ? 'text-green-600' : 'text-muted-foreground'}>
                    ✓ Mindestens eine Zahl
                  </p>
                </div>
              )}
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  className={errors.confirmPassword ? "border-destructive pl-10 pr-10" : "pl-10 pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
            </div>

            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptTerms"
                  checked={registerData.acceptTerms}
                  onCheckedChange={(checked) => setRegisterData({ ...registerData, acceptTerms: !!checked })}
                  className="mt-1"
                />
                <Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
                  Ich akzeptiere die{" "}
                  <Link to="/agb" target="_blank" className="text-primary hover:underline">
                    AGB
                  </Link>{" "}
                  *
                </Label>
              </div>
              {errors.acceptTerms && <p className="text-sm text-destructive">{errors.acceptTerms}</p>}

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptPrivacy"
                  checked={registerData.acceptPrivacy}
                  onCheckedChange={(checked) => setRegisterData({ ...registerData, acceptPrivacy: !!checked })}
                  className="mt-1"
                />
                <Label htmlFor="acceptPrivacy" className="text-sm leading-relaxed">
                  Ich akzeptiere die{" "}
                  <Link to="/datenschutz" target="_blank" className="text-primary hover:underline">
                    Datenschutzerklärung
                  </Link>{" "}
                  *
                </Label>
              </div>
              {errors.acceptPrivacy && <p className="text-sm text-destructive">{errors.acceptPrivacy}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Konto wird erstellt...
                </>
              ) : (
                "Konto erstellen und fortfahren"
              )}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh]">
          <SheetHeader>
            <SheetTitle>Anmelden oder Registrieren</SheetTitle>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Anmelden oder Registrieren</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}